import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';

import { Router } from '@angular/router';
import { UsuariosService } from '@app/core/services/usuario.service';
import { DescripcionUsuario } from '@app/interfaces/general/descripcion-usuario';
import { Genero } from '@app/interfaces/general/genero.interface';
import { Nacionalidad } from '@app/interfaces/general/nacionalidad.interface';
import { Empresa } from '@app/models/empresa';
import { Linea } from '@app/models/linea';
import { Usuario } from '@app/models/usuario';
import { ConfirmationService, MessageService, TreeNode } from 'primeng/api';
import { environment } from 'src/environments/environment';
import { Rol } from '@app/models/rol';
import { ModulesService } from '@app/core/services/modules.service';
import { TipoPlanillaService } from '@app/core/services/tipo-planilla.service';
import { catchError, forkJoin, of, switchMap } from 'rxjs';
import { ConductorService } from '@app/core/services/conductor.service';
import { TipoLicConductor } from '@app/models/tipo-lic-conductor';
import { NivelMD } from '@app/models/nivel-md';
import { Conductor } from '@app/models/conductor';
import { TipoPlanilla } from '@app/models/tipo-planilla';

interface RandomClaim {
    label: string;
    value: any;
    description?: string;
    id?: string;
    grupo?: string;
    subGrupo?: string;
}

interface Option {
    subOption: string;
    options: RandomClaim[];
}

interface GrupoGeneral {
    nombre: string;
    grupos: Option[];
}

interface OpcionesSelect {
    lineas: Linea[];
    descripciones: DescripcionUsuario[];
    generos: Genero[];
    trabajaLaV: { label: string; value: any }[];
    nacionalidades: Nacionalidad[];
    empresas: Empresa[];
    grupos: GrupoGeneral[];
}

@Component({
    selector: 'app-usuario-form',
    providers: [],
    templateUrl: './usuario-form.component.html',
    styleUrl: './usuario-form.component.scss',
    standalone: false
})
export class UsuarioFormComponent implements OnInit, OnDestroy {
    // servicios
    private readonly config = inject(DynamicDialogConfig, { optional: true });
    private readonly ref = inject(DynamicDialogRef, { optional: true });

    private readonly service = inject(UsuariosService);
    private readonly messageService = inject(MessageService);
    private readonly confirmationService = inject(ConfirmationService);
    private readonly router = inject(Router);
    private readonly modulesSvc = inject(ModulesService);
    private readonly conductorService = inject(ConductorService);
    private readonly tipoPlanillaService = inject(TipoPlanillaService);

    genero: any;
    // estado
    private listener: any;
    esEdicion = false;
    usuario: Usuario = {} as any;
    visible = false;

    selectedFile: File | undefined;
    esVista = false;
    imagenPreview: string | ArrayBuffer | null = null;
    sendWelcomeEmail = true;
    private bypassUnsavedConfirm = false;
    private originalDialogClose?: (result?: any) => void;

    ngOnInit() {
        this.esVista = !this.config;
        this.esEdicion = this.config?.data.id ? true : false;

        this.hookDialogCloseWithUnsavedConfirm();

        this.cargarOpciones();
    }

    private hookDialogCloseWithUnsavedConfirm(): void {
        if (!this.ref || !this.esEdicion || this.originalDialogClose) return;

        this.originalDialogClose = this.ref.close.bind(this.ref);
        this.ref.close = (result?: any) => {
            if (this.bypassUnsavedConfirm || !this.hasUnsavedChanges()) {
                this.originalDialogClose?.(result);
                return;
            }

            this.confirmationService.confirm({
                message: '¿Está seguro que quiere salir? Perderá los datos ingresados aquí.',
                header: 'Datos Sin Guardar',
                icon: 'pi pi-exclamation-triangle',
                acceptLabel: 'Salir',
                rejectLabel: 'Cancelar',
                accept: () => {
                    this.form.markAsPristine();
                    this.conductorForm.markAsPristine();
                    this.selectedFile = undefined;
                    this.originalDialogClose?.(result);
                },
                reject: () => {}
            });
        };
    }

    private hasUnsavedChanges(): boolean {
        return this.form.dirty || this.conductorForm.dirty || !!this.selectedFile;
    }

    private cargarOpciones() {
        // Obtenemos los datos iniciales
        forkJoin({
            form: this.service.initForm(),
            modules: this.modulesSvc.getModules(),
            conductor: this.conductorService.initForm(),
            aprobacionTipos: this.tipoPlanillaService.getAprobacionTipos({ modulo: 'Solicitudes (RH)' }).pipe(catchError(() => of({ entities: [] as TipoPlanilla[], count: 0 })))
        }).subscribe({
            next: (res) => {
                this.opcionesSelect.lineas = res.form.lineas;
                this.opcionesSelect.empresas = res.form.empresas;
                this.opcionesSelect.descripciones = res.form.descripciones;
                this.opcionesSelect.nacionalidades = res.form.nacionalidades;
                this.opcionesSelect.generos = res.form.generos;
                this.conductorOptions.tipoLicencia = res.conductor.tipoLicencia;
                this.conductorOptions.tipoConducir = res.conductor.tipoConducir;
                this.conductorOptions.nivelMd = res.conductor.nivel;
                this.rrhhAprobacionTipos = res.aprobacionTipos.entities ?? [];

                // Slugs de módulos activos (para filtrar grupos de permisos)
                const activeModuleSlugs = new Set(
                    (res.modules || []).filter((m) => m.activo === 1).map((m) => (m.url || '').replace(/^\/+/, '').toLowerCase())
                );

                this.opcionesSelect.grupos = this.generarGrupos(res.form.roles, activeModuleSlugs);

                if (this.esEdicion) {
                    // Y en el caso que sea edición, que busque el dato correspondiente
                    const id = this.config?.data.id;
                    this.service.getOne(id).subscribe({
                        next: (data) => {
                            const rrhhAprobacionTipoPlanillaIds = ((data as any)['permisosDeAprobacion'] || [])
                                .filter((permiso: any) => {
                                    const moduloNombre = (permiso.modulo?.nombre_modulo || permiso.modulo?.nombre || '').toString().toLowerCase();
                                    const submoduloNombre = (permiso.submodulo?.nombre || '').toString().toLowerCase();
                                    return moduloNombre.includes('solicitudes') || submoduloNombre.includes('solicitudes');
                                })
                                .map((permiso: any) => permiso.tipoPlanilla?.id)
                                .filter(Boolean);

                            this.usuario = data;
                            if (data.imagen_perfil) this.imagenPreview = environment.imagesUrl + 'avatar/' + data.imagen_perfil;
                            this.form.patchValue({
                                ...data,
                                fecha_ingreso: new Date(data.fecha_ingreso),
                                fecha_nacimiento: new Date(data.fecha_nacimiento),
                                empresa: this.opcionesSelect.empresas.find((empresa) => empresa.id == (data.empresa?.id ?? '')),
                                nacionalidad: this.opcionesSelect.nacionalidades.find((nacionalidad) => nacionalidad.id == (data.nacionalidad?.id ?? '')),
                                linea: this.opcionesSelect.lineas.find((linea) => linea.id == (data.linea?.id ?? '')),
                                genero: this.opcionesSelect.generos.find((genero) => genero.id == (data.genero?.id ?? '')),
                                descripcion: this.opcionesSelect.descripciones.find((descripcion) => descripcion.id == (data.descripcion?.id ?? '')),
                                randomClaims: data.randomClaims
                            });
                            this.rrhhAprobacionTipoPlanillaIds = rrhhAprobacionTipoPlanillaIds;
                            this.form.get('rrhhAprobacionTipoPlanillaIds')?.setValue(rrhhAprobacionTipoPlanillaIds);
                            // Cargar datos del conductor si existe
                            this.conductorService.getOne(data.id).subscribe({
                                next: (conductor) => {
                                    if (conductor) {
                                        this.conductorId = conductor.id as any;
                                        this.conductorForm.patchValue({
                                            nro_licencia: conductor.nro_licencia ?? null,
                                            licencia_tipo: this.conductorOptions.tipoLicencia.find((t) => t.id == conductor.licencia_tipo?.id) ?? null,
                                            vencimiento_licencia: conductor.vencimiento_licencia ? new Date(conductor.vencimiento_licencia) : null,
                                            llave: conductor.llave ?? null,
                                            tipo: this.conductorOptions.tipoConducir.find((t) => t.id == conductor.tipo?.id) ?? null,
                                            md_nivel: this.conductorOptions.nivelMd.find((m) => m.id == conductor.md_nivel?.id) ?? null
                                        });
                                    }
                                },
                                error: () => {} // El conductor puede no tener datos cargados aún
                            });
                        },
                        error: (err) => {
                            console.error('Error al traer el usuario', err);
                        }
                    });
                }
            },
            error: (err) => {
                console.error('Error cargando opciones', err);
                this.messageService.add({ severity: 'error', summary: 'Error', detail: 'No se pudieron cargar las opciones.' });
            }
        });
    }

    generarGrupos(roles: Rol[], activeModuleSlugs: Set<string> = new Set()): GrupoGeneral[] {
        // Mapeo de padreId a slug de módulo en BD (para comparar con module.url)
        const padreIdToSlug: Record<string, string | null> = {
            HSE: 'hse',
            RRHH: 'rrhh',
            MA: 'medio-ambiente',
            SISTEMA: null // especial: siempre visible, no depende de un módulo
        };

        // Obtener los grupos principales (HSE, RRHH, SISTEMA, etc.)
        const padres = [...new Set(roles.map((r) => r.padreId).filter(Boolean))];

        return padres
            .filter((padre) => {
                const slug = padreIdToSlug[padre];
                // Si no tiene mapeo conocido o es SISTEMA (null), siempre mostrar
                if (slug === null || slug === undefined) return true;
                // Si el slug no está en los módulos activos, ocultarlo
                return activeModuleSlugs.size === 0 || activeModuleSlugs.has(slug);
            })
            .map((padre) => ({
                nombre: padre,
                grupos: this.filterGrupos(padre, roles)
            }));
    }

    private filterGrupos(name: string, roles: Rol[]) {
        const allClaims = roles
            .filter((rol) => rol.padreId === name)
            .flatMap((rol) => rol.claims);
        const uniqueClaims = Array.from(
            new Map(allClaims.map((c) => [c.nombre, c])).values()
        );
        const grupos = Object.values(
            uniqueClaims
                .reduce(
                    (acc, claim) => {
                        // Agrupa por las dos primeras partes del nombre
                        const grupoId = claim.nombreShort;

                        if (!acc[grupoId]) {
                            acc[grupoId] = {
                                subOption: grupoId,
                                options: []
                            };
                        }

                        acc[grupoId].options.push({
                            label: claim.etiqueta,
                            value: claim.id ?? claim.nombre, // por si claim.id no existe
                            description: claim.descripcion,
                            id: claim.nombre
                        });

                        return acc;
                    },
                    {} as Record<string, Option>
                )
        );
        return grupos;
    }

    onFileSelected(event: any): void {
        const file: File = event.target?.files?.[0];
        if (!file) return;

        this.selectedFile = file;

        const reader = new FileReader();
        reader.onload = () => (this.imagenPreview = reader.result);
        reader.readAsDataURL(file);
    }

    ngOnDestroy() {
        document.removeEventListener('abrir-ayuda', this.listener);
    }

    // === ACCIONES ===

    /** Extrae un mensaje legible del error HTTP devuelto por la API */
    private parseApiError(err: any, fallback: string): string {
        const body = err?.error;
        if (!body) return fallback;
        // NestJS devuelve { message, statusCode } o string directo
        if (typeof body === 'string') return body;
        if (typeof body.message === 'string') return body.message;
        if (Array.isArray(body.message)) return body.message.join(' · ');
        // Errores SQL de TypeORM suelen venir en body.sqlMessage o body.driverError.sqlMessage
        const sql = body.sqlMessage ?? body.driverError?.sqlMessage;
        if (sql) return `Error de base de datos: ${sql}`;
        return fallback;
    }

    conductorId: string | null = null;

    update() {
        if (this.form.invalid) return;
        if (!this.hasSelectedClaims()) {
            this.form.get('randomClaims')?.markAsTouched();
            this.messageService.add({
                severity: 'warn',
                summary: 'Permisos requeridos',
                detail: 'Debe seleccionar al menos un permiso para guardar el usuario.'
            });
            return;
        }
        if (this.isConductorSelected && this.conductorForm.invalid) {
            this.conductorForm.markAllAsTouched();
            return;
        }
        if (this.hasRrhhApprovalClaim() && !this.hasRrhhAprobacionTipos) {
            this.form.get('rrhhAprobacionTipoPlanillaIds')?.markAsTouched();
            this.messageService.add({
                severity: 'warn',
                summary: 'Tipos de solicitud requeridos',
                detail: 'Para el permiso de aprobador RRHH debe seleccionar al menos un tipo de solicitud.'
            });
            return;
        }

        const { ...datos } = { ...this.usuario, ...this.form.value };

        const formData = new FormData();

        if (this.selectedFile) formData.append('file', this.selectedFile);

        formData.append('form', JSON.stringify(datos));

        this.service.update(this.usuario.id, formData).pipe(
            switchMap((respuesta) => {
                if (!this.isConductorSelected) return of(respuesta);

                const conductorId$ = this.conductorId
                    ? of(this.conductorId)
                    : this.conductorService.syncFromUsuario(this.usuario.id).pipe(
                          switchMap((c) => { this.conductorId = c.id as any; return of(c.id as string); })
                      );

                return conductorId$.pipe(
                    switchMap((cid) => {
                        const update: Partial<Conductor> = {
                            id: cid as any,
                            nro_licencia: this.conductorForm.value.nro_licencia!,
                            licencia_tipo: (this.conductorForm.value.licencia_tipo as any)?.id,
                            vencimiento_licencia: this.conductorForm.value.vencimiento_licencia!,
                            llave: this.conductorForm.value.llave!,
                            tipo: (this.conductorForm.value.tipo as any)?.id,
                            md_nivel: (this.conductorForm.value.md_nivel as any)?.id
                        };
                        return this.conductorService.update(update);
                    })
                );
            })
        ).subscribe({
            next: (respuesta) => {
                this.messageService.add({
                    severity: 'success',
                    summary: 'Actualizado',
                    detail: `El usuario ${datos.usuario} fue actualizado exitosamente.`
                });
                this.bypassUnsavedConfirm = true;
                this.form.markAsPristine();
                this.conductorForm.markAsPristine();
                this.selectedFile = undefined;
                this.ref?.close(respuesta);
            },
            error: (err) => {
                console.error('Error en actualización:', err);
                const msg = this.parseApiError(err, 'No se pudo actualizar el usuario.');
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error al guardar',
                    detail: msg,
                    life: 8000
                });
            }
        });
    }

    crearUsuario() {
        if (this.form.invalid) return;
        if (!this.hasSelectedClaims()) {
            this.form.get('randomClaims')?.markAsTouched();
            this.messageService.add({
                severity: 'warn',
                summary: 'Permisos requeridos',
                detail: 'Debe seleccionar al menos un permiso para crear el usuario.'
            });
            return;
        }
        if (this.isConductorSelected && this.conductorForm.invalid) {
            this.conductorForm.markAllAsTouched();
            return;
        }
        if (this.hasRrhhApprovalClaim() && !this.hasRrhhAprobacionTipos) {
            this.form.get('rrhhAprobacionTipoPlanillaIds')?.markAsTouched();
            this.messageService.add({
                severity: 'warn',
                summary: 'Tipos de solicitud requeridos',
                detail: 'Para el permiso de aprobador RRHH debe seleccionar al menos un tipo de solicitud.'
            });
            return;
        }

        const datos = { ...this.usuario, ...this.form.value, sendWelcomeEmail: this.sendWelcomeEmail };

        const formData = new FormData();

        formData.append('form', JSON.stringify(datos));

        if (this.selectedFile) formData.append('file', this.selectedFile);

        this.service.register(formData).pipe(
            switchMap((resp: any) => {
                if (this.isConductorSelected) {
                    const userId = resp.result?.id;
                    return this.conductorService.syncFromUsuario(userId).pipe(
                        switchMap((conductor) => {
                            const update: Partial<Conductor> = {
                                id: conductor.id as any,
                                nro_licencia: this.conductorForm.value.nro_licencia!,
                                licencia_tipo: (this.conductorForm.value.licencia_tipo as any)?.id,
                                vencimiento_licencia: this.conductorForm.value.vencimiento_licencia!,
                                llave: this.conductorForm.value.llave!,
                                tipo: (this.conductorForm.value.tipo as any)?.id,
                                md_nivel: (this.conductorForm.value.md_nivel as any)?.id
                            };
                            return this.conductorService.update(update);
                        }),
                        catchError((conductorErr) => {
                            console.warn('El usuario fue creado, pero hubo un error al configurar el conductor:', conductorErr);
                            this.messageService.add({
                                severity: 'warn',
                                summary: 'Atención',
                                detail: 'El usuario fue creado, pero no se pudieron guardar los datos del conductor. Puede editarlos manualmente.',
                                life: 10000
                            });
                            return of(resp);
                        })
                    );
                }
                return of(resp);
            })
        ).subscribe({
            next: (_) => {
                this.messageService.add({
                    severity: 'success',
                    summary: 'Creado',
                    detail: `El usuario ${datos.usuario} fue creado exitosamente.`
                });
                this.router.navigate(['/usuarios/historico']);
            },
            error: (err) => {
                console.error('Error al crear usuario', err);
                const status = err.status;
                if (status === 409) {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Datos duplicados',
                        detail: 'El nickname, correo o DNI ya está registrado en el sistema.',
                        life: 8000
                    });
                } else {
                    const msg = this.parseApiError(err, 'No se pudo crear el usuario.');
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Error al crear usuario',
                        detail: msg,
                        life: 8000
                    });
                }
            }
        });
    }

    cancelar() {
        if (this.esEdicion) this.ref?.close();
    }

    // === FORM ===
    form = new FormGroup({
        usuario: new FormControl(this.usuario.usuario, [Validators.required, Validators.minLength(3)]),
        dni: new FormControl(this.usuario.dni, [Validators.min(1)]),
        nombre: new FormControl(this.usuario.nombre, Validators.required),
        apellido: new FormControl(this.usuario.apellido, Validators.required),
        clave: new FormControl(this.usuario.clave),
        linea: new FormControl(this.usuario.linea),
        telefono: new FormControl(this.usuario.telefono),
        direccion: new FormControl(this.usuario.direccion),
        empresa: new FormControl(this.usuario.empresa, Validators.required),
        correo: new FormControl(this.usuario.correo, [Validators.required, Validators.email]),
        descripcion: new FormControl(this.usuario.descripcion),
        nacionalidad: new FormControl(this.usuario.nacionalidad),
        legajo: new FormControl(this.usuario.legajo),
        cuil: new FormControl(this.usuario.cuil, [
            Validators.maxLength(11),
            Validators.pattern(/^\d{11}$/) // solo números y 11 dígitos
        ]),
        fecha_nacimiento: new FormControl(this.usuario.fecha_nacimiento),
        genero: new FormControl(this.usuario.genero),
        fecha_ingreso: new FormControl(this.usuario.fecha_ingreso),
        randomClaims: new FormControl(this.usuario.randomClaims || [], [this.claimsRequiredValidator]),
        rrhhAprobacionTipoPlanillaIds: new FormControl<string[]>([])
    });

    private claimsRequiredValidator(control: AbstractControl): ValidationErrors | null {
        const value = control.value;
        return Array.isArray(value) && value.length > 0 ? null : { required: true };
    }

    hasSelectedClaims(): boolean {
        const claims = this.form.get('randomClaims')?.value;
        return Array.isArray(claims) && claims.length > 0;
    }

    private hasRrhhApprovalClaim(): boolean {
        return this.hasRrhhAprobadorClaim;
    }

    opcionesSelect: OpcionesSelect = {
        lineas: [],
        descripciones: [],
        nacionalidades: [],
        empresas: [],
        generos: [],
        trabajaLaV: [
            { label: 'Si', value: 'si' },
            { label: 'No', value: 'no' }
        ],

        // values únicos
        grupos: []
    };

    // Menús informativos (para el accordion del template)
    menuHSE = [
        { header: 'Dashboard', items: [] as string[] },
        { header: 'Calendario', items: [] as string[] },
        {
            header: 'Gestión Vehicular',
            items: ['Crear Gerenciamiento', 'Histórico', 'Nueva Unidad', 'Todas las Unidades', 'Todos los Conductores', 'Nueva Poliza', 'Todas las polizas']
        },
        { header: 'Tarjeta de Observacion', items: ['Nueva Tarjeta', 'Histórico'] },
        { header: 'Reportes Admin', items: ['Estado Credenciales MD'] },
        { header: 'Parámetros', items: ['Línea de Servicio', 'Imágenes Dashboard'] },
        { header: 'Usuarios', items: ['Nuevo Usuario', 'Todos los Usuarios'] }
    ];

    menuAprobador = [
        { header: 'Dashboard', items: [] as string[] },
        { header: 'Calendario', items: [] as string[] },
        { header: 'Gestión Vehicular', items: ['Crear Gerenciamiento', 'Histórico', 'Histórico', 'Todas las unidades', 'Todas las polizas'] },
        { header: 'Tarjetas de Observación', items: ['Nueva Tarjeta', 'Histórico'] }
    ];

    menuConductor = [
        { header: 'Dashboard', items: [] as string[] },
        { header: 'Gestión Vehicular', items: ['Crear Gerenciamiento', 'Histórico', 'Histórico'] },
        { header: 'Tarjeta de Observación', items: ['Nueva Tarjeta', 'Histórico'] }
    ];

    menuGuardia = [
        { header: 'Dashboard', items: [] as string[] },
        { header: 'Gestión Vehicular', items: ['Crear Gerenciamiento', 'Histórico', 'Histórico'] }
    ];

    // Keys únicos en p-tree (evita colisiones)
    permisos: TreeNode[] = [
        {
            label: 'HSE',
            key: 'hse',
            children: [
                { label: 'Administracion HSE', key: 'hse-administracionHSE' },
                { label: 'Administración Sistema', key: 'hse-administracionSistema' },
                {
                    label: 'Gestión Vehicular y HSE',
                    key: 'hse-gestionVehicular',
                    children: [
                        { label: 'Aprobador/a GV Turnos', key: 'hse-aprobadorGV' },
                        { label: 'Dashboard GVD', key: 'hse-dashboardGVD' },
                        { label: 'Modulo CheckList Completo', key: 'hse-checklist' }
                    ]
                },
                {
                    label: 'Inspector/a',
                    key: 'hse-inspector',
                    children: [
                        { label: 'Dashboard INS', key: 'hse-dashboardINS' },
                        { label: 'Modulo CheckList Completo', key: 'hse-checklist-inspector' },
                        { label: 'Dashboard SOP', key: 'hse-dashboardSOP' },
                        { label: 'Aprobador/a', key: 'hse-aprobador' },
                        { label: 'Checklists (sólo carga)', key: 'hse-listasVerificacion' },
                        { label: 'Dashboard Des', key: 'hse-dashboardDES' },
                        { label: 'Editor/a SOP', key: 'hse-editorSOP' }
                    ]
                },
                { label: 'Guardia', key: 'hse-guardia' },
                { label: 'Juegos Lúdicos', key: 'hse-juegos' },
                { label: 'Juegos Lúdicos (admin)', key: 'hse-juegosAdmin' },
                { label: 'Operador/a', key: 'hse-operador' },
                { label: 'Operador/a (sólo lectura)', key: 'hse-operadorLectura' }
            ]
        },
        {
            label: 'RRHH',
            key: 'rrhh',
            children: [
                { label: 'Administración RH', key: 'rrhh-administracionRH' },
                { label: 'Aprobador/a Solicitudes RH', key: 'rrhh-aprobadorRH' }
            ]
        }
    ];

    selectedPermisos: TreeNode[] = [];

    // Conductor
    conductorForm = new FormGroup({
        nro_licencia: new FormControl<number | null>(null, [Validators.required, Validators.max(9_999_999_999)]),
        licencia_tipo: new FormControl<TipoLicConductor | null>(null, [Validators.required]),
        vencimiento_licencia: new FormControl<Date | null>(null, [Validators.required]),
        llave: new FormControl<string | null>(null),
        tipo: new FormControl<TipoLicConductor | null>(null),
        md_nivel: new FormControl<NivelMD | null>(null)
    });

    conductorOptions: { tipoLicencia: TipoLicConductor[]; tipoConducir: TipoLicConductor[]; nivelMd: NivelMD[] } = {
        tipoLicencia: [],
        tipoConducir: [],
        nivelMd: []
    };

    rrhhAprobacionTipos: TipoPlanilla[] = [];
    rrhhAprobacionTipoPlanillaIds: string[] = [];

    get isConductorSelected(): boolean {
        const claims = this.form.get('randomClaims')?.value ?? [];
        return claims.some((c: any) => c.id === 'HSE_GERENCIAMIENTO_VIAJE_CONDUCTOR');
    }

    get hasRrhhAprobadorClaim(): boolean {
        const claims = this.form.get('randomClaims')?.value ?? [];
        return claims.some((c: any) => c.id === 'RRHH_SOLICITUDES_APROBAR' || c.value === 'RRHH_SOLICITUDES_APROBAR');
    }

    get hasRrhhAprobacionTipos(): boolean {
        return this.rrhhAprobacionTipoPlanillaIds.length > 0;
    }

    private setRrhhAprobacionTipoPlanillaIds(ids: string[]): void {
        this.rrhhAprobacionTipoPlanillaIds = ids;
        this.form.get('rrhhAprobacionTipoPlanillaIds')?.setValue(ids);
    }

    onSelectionChange(_: any) {
        // hook para mapear si hace falta
    }

    // SELECCIÓN DE GRUPOS (HSE / RRHH)
    toggleGrupo(grupo: string, subGrupo: string, claimId: string) {
        const control = this.form.get('randomClaims');
        const userClaims = control?.value ?? [];

        const existClaim = userClaims.find((c) => c.value === claimId);

        if (existClaim) {
            const updatedClaims = userClaims.filter((c) => c.value !== claimId);
            control?.setValue(updatedClaims);
            if (claimId === 'RRHH_SOLICITUDES_APROBAR') {
                this.setRrhhAprobacionTipoPlanillaIds([]);
            }
            return;
        }

        const findClaim = this.opcionesSelect.grupos
            .find((g) => g.nombre === grupo)
            ?.grupos.find((sg) => sg.subOption === subGrupo)
            ?.options.find((op) => op.value === claimId);

        if (findClaim) {
            const newClaim = { ...findClaim, grupo, subGrupo };
            const updatedClaims = [...userClaims, newClaim];
            control?.setValue(updatedClaims);
        }
    }

    toggleRrhhAprobacionTipo(tipoId: string): void {
        const exists = this.rrhhAprobacionTipoPlanillaIds.includes(tipoId);
        const updated = exists
            ? this.rrhhAprobacionTipoPlanillaIds.filter((id) => id !== tipoId)
            : [...this.rrhhAprobacionTipoPlanillaIds, tipoId];

        this.setRrhhAprobacionTipoPlanillaIds(updated);
    }

    isRrhhAprobacionTipoSelected(tipoId: string): boolean {
        return this.rrhhAprobacionTipoPlanillaIds.includes(tipoId);
    }

    private syncRrhhAprobacionControl(): void {
        if (!this.hasRrhhAprobadorClaim) {
            this.setRrhhAprobacionTipoPlanillaIds([]);
        }
    }
    checkButtonClaim(grupo: string, subGrupo: string, claimId: string) {
        const control = this.form.get('randomClaims');
        const userClaims = control?.value ?? [];
        const existClaim = userClaims.find((c) => c.value === claimId && c.grupo == grupo && c.subGrupo == subGrupo);
        return existClaim ? true : false;
    }
}
